import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardList,
  faScrewdriverWrench,
  faCircleCheck,
  faNoteSticky,
  faCalendarDays,
  faIndianRupeeSign,
  faUsers,
  faWrench,
  faBrain,
  faHeart,
  faChartLine,
  faChartColumn,
} from "@fortawesome/free-solid-svg-icons";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const tabs = [
  { key: "requested", label: "Requested", icon: faClipboardList },
  { key: "suggested", label: "Suggested", icon: faScrewdriverWrench },
  { key: "done", label: "Completed", icon: faCircleCheck },
  { key: "instructions", label: "Instructions", icon: faNoteSticky },
  { key: "nextvisit", label: "Next Visit", icon: faCalendarDays },
  { key: "payments", label: "Payments", icon: faIndianRupeeSign },
  { key: "customers", label: "Customers", icon: faUsers },
  { key: "repairs", label: "My Repairs", icon: faWrench },
  { key: "ai", label: "AI Insights", icon: faBrain },
  { key: "thanks", label: "Thank You", icon: faHeart },
  { key: "trend", label: "Customer Trend", icon: faChartLine },
  { key: "business", label: "Business Graph", icon: faChartColumn },
];

/* Dummy Data – Realistic & Meaningful */
const repairRequests = [
  { id: 1, customer: "Rahul Sharma", phone: "+91 98765 43210", device: "iPhone 14 Pro", issue: "Cracked Screen & Battery Drain", requestedDate: "2026-01-02", address: "Sector 47, Gurgaon" },
  { id: 2, customer: "Meena Gupta", phone: "+91 87654 32109", device: "Samsung Double Door Refrigerator", issue: "Not Cooling, Strange Noise", requestedDate: "2026-01-01", address: "Sector 62, Noida" },
  { id: 3, customer: "Arjun Patel", phone: "+91 76543 21098", device: "Dell Inspiron Laptop", issue: "Blue Screen, Not Booting", requestedDate: "2026-01-02", address: "Dwarka, Delhi" },
  { id: 4, customer: "Priya Singh", phone: "+91 65432 10987", device: "LG Front Load Washing Machine", issue: "Drum Not Spinning", requestedDate: "2025-12-29", address: "Faridabad" },
  { id: 5, customer: "Vikram Rao", phone: "+91 54321 09876", device: "Sony 55\" LED TV", issue: "No Display, Sound OK", requestedDate: "2026-01-02", address: "South Delhi" },
  { id: 6, customer: "Neha Verma", phone: "+91 98760 12345", device: "OnePlus 11", issue: "Charging Port Damage", requestedDate: "2026-01-01", address: "Greater Noida" },
  { id: 7, customer: "Suresh Kumar", phone: "+91 87650 54321", device: "Voltas AC (1.5 Ton)", issue: "Low Cooling", requestedDate: "2025-12-31", address: "Ghaziabad" },
];

const completedRepairs = [
  { id: 101, customer: "Amit Kumar", device: "Whirlpool Washing Machine", issue: "Motor Replaced", completedDate: "2025-12-28", amount: "₹3,500", status: "Paid (UPI)" },
  { id: 102, customer: "Sonia Mehta", device: "Samsung LED TV", issue: "Backlight Repair", completedDate: "2025-12-26", amount: "₹8,000", status: "Paid (Cash)" },
  { id: 103, customer: "Ravi Sharma", device: "MacBook Air", issue: "Keyboard Replacement", completedDate: "2025-12-24", amount: "₹12,000", status: "Paid (UPI)" },
  { id: 104, customer: "Kavita Jain", device: "Godrej Refrigerator", issue: "Thermostat Fixed", completedDate: "2025-12-20", amount: "₹2,200", status: "Warranty – No Charge" },
];

const upcomingVisits = [
  { date: "2026-01-04", time: "10:00 AM", customer: "Rahul Sharma", service: "Screen Replacement", location: "Sector 47, Gurgaon" },
  { date: "2026-01-05", time: "02:30 PM", customer: "Meena Gupta", service: "Compressor Check & Gas Refill", location: "Sector 62, Noida" },
  { date: "2026-01-06", time: "11:30 AM", customer: "Suresh Kumar", service: "AC Servicing", location: "Ghaziabad" },
  { date: "2026-01-07", time: "04:00 PM", customer: "Priya Singh", service: "Washing Machine Repair Follow-up", location: "Faridabad" },
];

const recentPayments = [
  { date: "2026-01-02", customer: "Amit Kumar", amount: "₹3,500", mode: "UPI" },
  { date: "2026-01-01", customer: "Sonia Mehta", amount: "₹8,000", mode: "Cash" },
  { date: "2025-12-30", customer: "Ravi Sharma", amount: "₹12,000", mode: "UPI" },
  { date: "Pending", customer: "Meena Gupta", amount: "₹2,800 (est.)", mode: "—" },
  { date: "Pending", customer: "Rahul Sharma", amount: "₹4,500 (est.)", mode: "—" },
];

const customersList = [
  { name: "Rahul Sharma", phone: "+91 98765 43210", totalRepairs: 7, lastRepair: "2026-01-02", repeat: true },
  { name: "Meena Gupta", phone: "+91 87654 32109", totalRepairs: 3, lastRepair: "2026-01-01", repeat: true },
  { name: "Amit Kumar", phone: "+91 91234 56789", totalRepairs: 12, lastRepair: "2025-12-28", repeat: true },
  { name: "Priya Singh", phone: "+91 65432 10987", totalRepairs: 2, lastRepair: "2025-12-29", repeat: false },
  { name: "Vikram Rao", phone: "+91 54321 09876", totalRepairs: 4, lastRepair: "2025-11-15", repeat: true },
];

/* Chart Data */
const customerTrendData = {
  labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  datasets: [
    {
      label: "New Customers",
      data: [12, 18, 22, 28, 35, 42, 40, 45, 48, 55, 52, 58],
      borderColor: "#60a5fa",
      backgroundColor: "rgba(96, 165, 250, 0.2)",
      tension: 0.4,
      fill: true,
    },
    {
      label: "Repeat Customers",
      data: [8, 12, 15, 20, 25, 28, 32, 35, 38, 42, 45, 48],
      borderColor: "#34d399",
      backgroundColor: "rgba(52, 211, 153, 0.2)",
      tension: 0.4,
      fill: true,
    },
  ],
};

const businessData = {
  labels: ["Mobile", "Refrigerator", "Laptop", "Washing Machine", "AC", "TV", "Others"],
  datasets: [
    {
      label: "Jobs Completed",
      data: [58, 32, 28, 22, 30, 18, 12],
      backgroundColor: "#60a5fa",
    },
    {
      label: "Revenue (₹ thousands)",
      data: [145, 112, 98, 68, 105, 62, 35],
      backgroundColor: "#34d399",
    },
  ],
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "top", labels: { color: "#e0e0e0" } },
    title: { color: "#e0e0e0" },
  },
  scales: {
    x: { ticks: { color: "#e0e0e0" }, grid: { color: "rgba(255,255,255,0.05)" } },
    y: { ticks: { color: "#e0e0e0" }, grid: { color: "rgba(255,255,255,0.05)" } },
  },
};

const FixHubHome = () => {
  const [activeTab, setActiveTab] = useState("requested");

  // Search states
  const [searchRequested, setSearchRequested] = useState("");
  const [searchCompleted, setSearchCompleted] = useState("");
  const [searchVisits, setSearchVisits] = useState("");
  const [searchPayments, setSearchPayments] = useState("");
  const [searchCustomers, setSearchCustomers] = useState("");

  return (
    <section className="min-h-screen bg-black text-blue-300 px-3 py-4">
      {/* Header */}
      <header className="mb-6 text-center">
        <h1 className="text-2xl font-bold text-blue-400">
          🔧 FixHub – Repair Command Center
        </h1>
        <p className="text-sm text-blue-500 mt-1">
          Manage repairs, customers & insights in one place
        </p>
      </header>

      {/* Tabs Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        {tabs.map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex flex-col items-center justify-center py-4 px-2 rounded-xl border transition-all
              ${
                activeTab === key
                  ? "bg-blue-600 text-white border-blue-400 shadow-lg"
                  : "bg-zinc-900 text-blue-300 border-zinc-700 hover:bg-zinc-800 hover:border-zinc-600"
              }`}
          >
            <FontAwesomeIcon icon={icon} className="text-2xl mb-2" />
            <span className="text-xs text-center leading-tight">{label}</span>
          </button>
        ))}
      </div>

      {/* Content Panel */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 text-sm space-y-6">
        {/* Requested */}
        {activeTab === "requested" && (
          <div>
            <h2 className="font-semibold text-blue-400 mb-4">Repair Requests ({repairRequests.length})</h2>
            <input
              type="text"
              placeholder="Search customer, phone, device, issue, address..."
              value={searchRequested}
              onChange={(e) => setSearchRequested(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 mb-4 text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500"
            />
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {repairRequests
                .filter((item) => {
                  if (searchRequested === "") return true;
                  const search = searchRequested.toLowerCase();
                  return (
                    item.customer.toLowerCase().includes(search) ||
                    item.device.toLowerCase().includes(search) ||
                    item.issue.toLowerCase().includes(search) ||
                    item.phone.includes(searchRequested) ||
                    item.address.toLowerCase().includes(search)
                  );
                })
                .map((item) => (
                  <div key={item.id} className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <p className="font-medium text-white">{item.customer}</p>
                      <span className="text-xs text-zinc-400">{item.requestedDate}</span>
                    </div>
                    <p className="mt-1">{item.device} – {item.issue}</p>
                    <p className="text-xs text-zinc-400 mt-2">📞 {item.phone}</p>
                    <p className="text-xs text-zinc-400">📍 {item.address}</p>
                  </div>
                ))}
              {repairRequests
                .filter((item) => {
                  if (searchRequested === "") return true;
                  const search = searchRequested.toLowerCase();
                  return (
                    item.customer.toLowerCase().includes(search) ||
                    item.device.toLowerCase().includes(search) ||
                    item.issue.toLowerCase().includes(search) ||
                    item.phone.includes(searchRequested) ||
                    item.address.toLowerCase().includes(search)
                  );
                })
                .length === 0 && (
                  <p className="text-center text-zinc-500 py-12">No matching requests found</p>
                )}
            </div>
          </div>
        )}

        {/* Suggested */}
        {activeTab === "suggested" && (
          <div>
            <h2 className="font-semibold text-blue-400 mb-4">Suggested Repairs / Upsells</h2>
            <div className="space-y-3">
              <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
                <p className="font-medium text-white">Screen Replacement (Original)</p>
                <p className="text-blue-300">₹2,800 – ₹6,000</p>
                <p className="text-xs text-zinc-400 mt-1">Common for cracked screens</p>
              </div>
              <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
                <p className="font-medium text-white">Compressor Check + Gas Refill</p>
                <p className="text-blue-300">₹3,500 avg.</p>
                <p className="text-xs text-zinc-400 mt-1">Recommended for cooling issues</p>
              </div>
              <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
                <p className="font-medium text-white">Battery Replacement</p>
                <p className="text-blue-300">₹900 – ₹2,500</p>
                <p className="text-xs text-zinc-400 mt-1">For drain/swelling issues</p>
              </div>
              <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
                <p className="font-medium text-white">Protective Case / Tempered Glass</p>
                <p className="text-blue-300">₹300 – ₹800</p>
                <p className="text-xs text-zinc-400 mt-1">Prevent future damage</p>
              </div>
            </div>
          </div>
        )}

        {/* Completed */}
        {activeTab === "done" && (
          <div>
            <h2 className="font-semibold text-blue-400 mb-4">Completed Repairs</h2>
            <input
              type="text"
              placeholder="Search customer, device, issue..."
              value={searchCompleted}
              onChange={(e) => setSearchCompleted(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 mb-4 text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500"
            />
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {completedRepairs
                .filter((item) => {
                  if (searchCompleted === "") return true;
                  const search = searchCompleted.toLowerCase();
                  return (
                    item.customer.toLowerCase().includes(search) ||
                    item.device.toLowerCase().includes(search) ||
                    item.issue.toLowerCase().includes(search)
                  );
                })
                .map((item) => (
                  <div key={item.id} className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
                    <div className="flex justify-between">
                      <p className="font-medium text-white">{item.customer}</p>
                      <span className="text-xs text-zinc-400">{item.completedDate}</span>
                    </div>
                    <p>{item.device} – {item.issue}</p>
                    <p className="mt-2 text-green-400">{item.amount} • {item.status}</p>
                  </div>
                ))}
              {completedRepairs
                .filter((item) => {
                  if (searchCompleted === "") return true;
                  const search = searchCompleted.toLowerCase();
                  return (
                    item.customer.toLowerCase().includes(search) ||
                    item.device.toLowerCase().includes(search) ||
                    item.issue.toLowerCase().includes(search)
                  );
                })
                .length === 0 && (
                  <p className="text-center text-zinc-500 py-12">No matching completed repairs found</p>
                )}
            </div>
          </div>
        )}

        {/* Instructions */}
        {activeTab === "instructions" && (
          <div>
            <h2 className="font-semibold text-blue-400 mb-4">Special Instructions & Guidelines</h2>
            <ul className="space-y-3 list-disc list-inside">
              <li>Call customer 30 minutes before arrival</li>
              <li>Verify warranty status before quoting price</li>
              <li>Use genuine parts for Apple/Samsung flagship devices</li>
              <li>Take before & after photos for every repair</li>
              <li>Offer protective accessories to mobile customers</li>
              <li>Clean appliance thoroughly before handover</li>
            </ul>
          </div>
        )}

        {/* Next Visit */}
        {activeTab === "nextvisit" && (
          <div>
            <h2 className="font-semibold text-blue-400 mb-4">Upcoming Visits</h2>
            <input
              type="text"
              placeholder="Search customer, service, location..."
              value={searchVisits}
              onChange={(e) => setSearchVisits(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 mb-4 text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500"
            />
            <div className="space-y-3">
              {upcomingVisits
                .filter((item) => {
                  if (searchVisits === "") return true;
                  const search = searchVisits.toLowerCase();
                  return (
                    item.customer.toLowerCase().includes(search) ||
                    item.service.toLowerCase().includes(search) ||
                    item.location.toLowerCase().includes(search)
                  );
                })
                .map((item, idx) => (
                  <div key={idx} className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
                    <p className="font-medium text-white">{item.date} – {item.time}</p>
                    <p>{item.customer} • {item.service}</p>
                    <p className="text-xs text-zinc-400 mt-1">📍 {item.location}</p>
                  </div>
                ))}
              {upcomingVisits
                .filter((item) => {
                  if (searchVisits === "") return true;
                  const search = searchVisits.toLowerCase();
                  return (
                    item.customer.toLowerCase().includes(search) ||
                    item.service.toLowerCase().includes(search) ||
                    item.location.toLowerCase().includes(search)
                  );
                })
                .length === 0 && (
                  <p className="text-center text-zinc-500 py-12">No matching visits found</p>
                )}
            </div>
          </div>
        )}

        {/* Payments */}
        {activeTab === "payments" && (
          <div>
            <h2 className="font-semibold text-blue-400 mb-4">Payments Overview</h2>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-zinc-800 p-4 rounded-lg text-center">
                <p className="text-xs text-zinc-400">Today Collected</p>
                <p className="text-2xl font-bold text-green-400">₹4,200</p>
              </div>
              <div className="bg-zinc-800 p-4 rounded-lg text-center">
                <p className="text-xs text-zinc-400">Pending</p>
                <p className="text-2xl font-bold text-orange-400">₹7,300</p>
              </div>
            </div>
            <input
              type="text"
              placeholder="Search customer..."
              value={searchPayments}
              onChange={(e) => setSearchPayments(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 mb-4 text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500"
            />
            <div className="space-y-3">
              {recentPayments
                .filter((item) => {
                  if (searchPayments === "") return true;
                  const search = searchPayments.toLowerCase();
                  return item.customer.toLowerCase().includes(search);
                })
                .map((item, idx) => (
                  <div key={idx} className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 flex justify-between">
                    <div>
                      <p className="font-medium text-white">{item.customer}</p>
                      <p className="text-xs text-zinc-400">{item.date}</p>
                    </div>
                    <div className="text-right">
                      <p className={item.date === "Pending" ? "text-orange-400" : "text-green-400"}>{item.amount}</p>
                      <p className="text-xs text-zinc-400">{item.mode}</p>
                    </div>
                  </div>
                ))}
              {recentPayments
                .filter((item) => {
                  if (searchPayments === "") return true;
                  const search = searchPayments.toLowerCase();
                  return item.customer.toLowerCase().includes(search);
                })
                .length === 0 && (
                  <p className="text-center text-zinc-500 py-12">No matching payments found</p>
                )}
            </div>
          </div>
        )}

        {/* Customers */}
        {activeTab === "customers" && (
          <div>
            <h2 className="font-semibold text-blue-400 mb-4">My Customers</h2>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-zinc-800 p-4 rounded-lg text-center">
                <p className="text-xs text-zinc-400">Total</p>
                <p className="text-2xl font-bold text-blue-400">128</p>
              </div>
              <div className="bg-zinc-800 p-4 rounded-lg text-center">
                <p className="text-xs text-zinc-400">Repeat Rate</p>
                <p className="text-2xl font-bold text-green-400">46%</p>
              </div>
            </div>
            <input
              type="text"
              placeholder="Search name or phone..."
              value={searchCustomers}
              onChange={(e) => setSearchCustomers(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 mb-4 text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500"
            />
            <div className="space-y-3">
              {customersList
                .filter((item) => {
                  if (searchCustomers === "") return true;
                  const search = searchCustomers.toLowerCase();
                  return (
                    item.name.toLowerCase().includes(search) ||
                    item.phone.includes(searchCustomers)
                  );
                })
                .map((item, idx) => (
                  <div key={idx} className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
                    <p className="font-medium text-white">{item.name}</p>
                    <p className="text-xs text-zinc-400">📞 {item.phone}</p>
                    <p className="mt-2">Total Repairs: {item.totalRepairs} • Last: {item.lastRepair}</p>
                    {item.repeat && <span className="text-xs text-green-400">🔄 Loyal Customer</span>}
                  </div>
                ))}
              {customersList
                .filter((item) => {
                  if (searchCustomers === "") return true;
                  const search = searchCustomers.toLowerCase();
                  return (
                    item.name.toLowerCase().includes(search) ||
                    item.phone.includes(searchCustomers)
                  );
                })
                .length === 0 && (
                  <p className="text-center text-zinc-500 py-12">No matching customers found</p>
                )}
            </div>
          </div>
        )}

        {/* My Repairs */}
        {activeTab === "repairs" && (
          <div>
            <h2 className="font-semibold text-blue-400 mb-4">My Repairs Performance</h2>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-zinc-800 p-4 rounded-lg text-center">
                <p className="text-xs text-zinc-400">This Month Jobs</p>
                <p className="text-2xl font-bold text-blue-400">38</p>
              </div>
              <div className="bg-zinc-800 p-4 rounded-lg text-center">
                <p className="text-xs text-zinc-400">Avg Closure Time</p>
                <p className="text-2xl font-bold text-green-400">1.6 days</p>
              </div>
              <div className="bg-zinc-800 p-4 rounded-lg text-center">
                <p className="text-xs text-zinc-400">Month Revenue</p>
                <p className="text-2xl font-bold text-green-400">₹1,05,000</p>
              </div>
              <div className="bg-zinc-800 p-4 rounded-lg text-center">
                <p className="text-xs text-zinc-400">Avg Ticket Size</p>
                <p className="text-2xl font-bold text-blue-400">₹2,763</p>
              </div>
            </div>
          </div>
        )}

        {/* AI Insights */}
        {activeTab === "ai" && (
          <div>
            <h2 className="font-semibold text-blue-400 mb-4">AI Insights 🤖</h2>
            <ul className="space-y-3">
              <li className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
                📈 AC & Refrigerator repairs spike in Mar–May – stock parts now
              </li>
              <li className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
                🔄 46% repeat customers – offer Annual Maintenance Contracts
              </li>
              <li className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
                📱 Weekend mobile screen damages highest – upsell cases/glass
              </li>
              <li className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
                💰 Average ticket size ₹2,763 – cleaning/add-on services boost revenue
              </li>
            </ul>
          </div>
        )}

        {/* Thank You */}
        {activeTab === "thanks" && (
          <div>
            <h2 className="font-semibold text-blue-400 mb-4">Thank You Message Template</h2>
            <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-6 text-center">
              <p className="text-lg">🙏 Thank you for trusting FixHub!</p>
              <p className="mt-4">Your device has been repaired with care using genuine parts.</p>
              <p className="mt-4 text-blue-300">We look forward to serving you again.</p>
              <p className="text-xs text-zinc-500 mt-6">
                (Auto-sent via WhatsApp/SMS after completion)
              </p>
            </div>
          </div>
        )}

        {/* Customer Trend Graph */}
        {activeTab === "trend" && (
          <div>
            <h2 className="font-semibold text-blue-400 mb-4">Customer Trend (2025)</h2>
            <div className="h-80 md:h-96 bg-zinc-800 rounded-lg p-4">
              <Line options={{ ...chartOptions, plugins: { ...chartOptions.plugins, title: { display: true, text: "Customer Acquisition Trend" } } }} data={customerTrendData} />
            </div>
          </div>
        )}

        {/* Business Graph */}
        {activeTab === "business" && (
          <div>
            <h2 className="font-semibold text-blue-400 mb-4">Business Performance by Category</h2>
            <div className="h-80 md:h-96 bg-zinc-800 rounded-lg p-4">
              <Bar options={{ ...chartOptions, plugins: { ...chartOptions.plugins, title: { display: true, text: "Jobs vs Revenue by Device Category" } } }} data={businessData} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FixHubHome;